import React, { useEffect } from 'react';
import { useHistory } from 'react-router';
import { useSettings } from '../../hooks/queryHooks';
import { useAppState, useStepsState } from '../../hooks/zustandHooks';
import Container from '../../components/container/Container';
import { exitApp } from '../../hooks/utils';
import { useTranslation } from 'react-i18next';
import { useCreateConsentMutation, useStoredInvitationMutation } from '../../hooks/mutationHooks';
import RecaptchaDisclaimer from '../../components/recaptcha/RecaptchaDisclaimer';
import Loader from '../../components/loader/Loader';
import { DATA_SOURCE_TYPE } from '../../types/DataSourceType';
import { WelcomePageTypes, WelcomePageProps } from './Data';
import { useCreditAccountsFetchStatusReceiver } from '../../hooks/useCreditAccountsFetchStatusReceiver';
import AdaptiveLogo from '../../components/adaptiveLogo/AdaptiveLogo';
import { localStorageContainer, userIdStorage } from '../../domain/services';
import { CIS_ACCOUNT_BEARER } from '../../constants/constants';
import { trackEvent } from '../../App';
import { EventTypes } from '../../utils/applicationInsights';

const WelcomeScreen = () => {
  const { settings } = useSettings();
  const { t } = useTranslation();
  const steps = useStepsState(state => state.steps);
  const { appState } = useAppState();
  const { useValidationToken, invitationId } = appState;
  const history = useHistory();
  const creditAccountsFetchStatusReceiver = useCreditAccountsFetchStatusReceiver();

  const createConsentMutation = useCreateConsentMutation();
  const createStoredUserMutation = useStoredInvitationMutation();

  const isDashboardCisInvite = appState?.invitationId && settings?.enableCreditInformationFlow;

  let pageType: WelcomePageTypes = 'OpenBankingFlow';
  if (isDashboardCisInvite) pageType = 'DashboardInviteCISFlow';
  else if (settings?.enableCreditInformationFlow) pageType = 'CreditInformationFlow';
  else if (appState.documentUpload) pageType = 'DocumentUploadFlow';

  const pageProps = WelcomePageProps[pageType];

  useEffect(() => {
    if (pageType === 'DashboardInviteCISFlow' || pageType === 'CreditInformationFlow') {
      const token = localStorageContainer.getItemByClientId(CIS_ACCOUNT_BEARER);
      if (token && localStorageContainer.isTokenExpired(token)) {
        trackEvent({
          type: EventTypes.CIS_LOCAL_STORAGE_TOKEN,
          payload: {
            tokenKey: CIS_ACCOUNT_BEARER,
            message: 'Token has expired'
          }
        });
        return;
      }
      const userId = userIdStorage.hasUserId();
      if (userId && token) {
        history.push('/tu-end-success', { hasValidToken: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageType, Object.keys(settings || {}).length]);

  const onClick = async () => {
    if (pageType === 'DashboardInviteCISFlow') {
      const { status = '' } = (await createStoredUserMutation.mutateAsync()) || {};
      if (status === 'OtpRequired') history.push('/verify-number');
      else if (status === 'KbaRequired') history.push('/kba');
      else if (status === 'Positive') history.push('/tu-end-success');
      else history.push('/account-lookup', { hasFailedValidation: true });

      return;
    }
    if (pageType === 'CreditInformationFlow') {
      await createConsentMutation.mutateAsync(
        {
          providerId: 0,
          name: 'CIS',
          dataSource: 'CIS',
          dataSourceType: DATA_SOURCE_TYPE.CreditInformation
        },
        {
          onSuccess: () => {
            history.push('/account-lookup');
          }
        }
      );
      await creditAccountsFetchStatusReceiver.connect();
      return;
    }
    if (pageType === 'DocumentUploadFlow') {
      history.push('/document-upload');
      return;
    }
    history.push('/bank-select');
  };

  const customerLogoContainer = (
    <div className="my-6 block xl:flex xl:flex-col xl:items-end">
      <p className="uppercase text-primary-2 text-xs sm:text-sm font-medium tracking-[0.6px] mb-3">
        {t('In partnership with')}
      </p>
      {settings?.logoUrl ? (
        <AdaptiveLogo src={settings.logoUrl} alt={settings?.customerName} />
      ) : (
        <span className="text-2xl text-gray-500 mt-1 tracking-wide uppercase block">{settings?.customerName}</span>
      )}
    </div>
  );

  if (createConsentMutation.isPending || createStoredUserMutation.isPending) {
    return (
      <Loader display={true} showBackButton spinnerText={invitationId ? t('Verifying identity...') : t('Loading...')} />
    );
  }

  return (
    <Container
      primaryBtn={{
        text: t('Continue'),
        onClick: () => onClick()
      }}
      {...(settings?.redirectUrl && {
        secondaryBtn: {
          text: t('Abort & return to partner'),
          onClick: () => exitApp()
        }
      })}
    >
      <div className="w-full h-full flex flex-col justify-between font-RoobertPRO">
        <div className="text-primary-2 h-auto">
          <h1 className="text-lg sm:text-2xl sm:block mb-2 font-semibold uppercase">{pageProps.title}</h1>
          {pageProps.description({ customerName: settings?.customerName })}
        </div>
        <div className="grow flex flex-col justify-between gap-4">
          <div className="block xl:hidden">{customerLogoContainer}</div>
          <section className="grow p-6 mb-4 bg-gray-100 rounded-lg min-h-80 block xl:hidden">
            <h2 className="uppercase text-base font-semi-bold mb-6">{t('How the process works')}</h2>
            {steps.map(step => {
              return (
                <div key={step.stepIndex} className="mb-6">
                  <h3 className="text-lg text-primary-2 font-semi-bold uppercase mb-2">{t(step.title || '')}</h3>
                  <p className="text-base text-gray-800 font-normal my-0!">{t(step.description || '')}</p>
                </div>
              );
            })}
          </section>
        </div>

        <div className="xl:block hidden">{customerLogoContainer}</div>
        {settings?.enableCreditInformationFlow && !useValidationToken && <RecaptchaDisclaimer />}
      </div>
    </Container>
  );
};

export default WelcomeScreen;
